import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CHORE_THEMES, THEME_KEYS, fmtMoney } from "@/lib/themes";
import { toast } from "sonner";

export function ManageTab({ onData }) {
  const { members, refreshMembers } = useAuth();
  const [chores, setChores] = useState([]);
  const [choreForm, setChoreForm] = useState({ title: "", theme: "washing_up", reward: 1.5 });
  const [editingChore, setEditingChore] = useState(null);
  const [memberForm, setMemberForm] = useState({ name: "", role: "kid", avatar: "🧒" });

  const loadChores = async () => setChores((await api.get("/chores")).data);
  useEffect(() => { loadChores(); }, []);

  const saveChore = async (e) => {
    e.preventDefault();
    if (!choreForm.title) return toast.error("Give the chore a name");
    const payload = { ...choreForm, reward: parseFloat(choreForm.reward) || 0 };
    if (editingChore) await api.patch(`/chores/${editingChore}`, payload);
    else await api.post("/chores", payload);
    setChoreForm({ title: "", theme: "washing_up", reward: 1.5 });
    setEditingChore(null);
    loadChores();
    toast.success(editingChore ? "Chore updated" : "Chore added to the wheel 🎡");
  };
  const editChore = (c) => { setEditingChore(c.id); setChoreForm({ title: c.title, theme: c.theme, reward: c.reward }); };
  const delChore = async (id) => { await api.delete(`/chores/${id}`); loadChores(); };

  const addMember = async (e) => {
    e.preventDefault();
    if (!memberForm.name) return toast.error("Enter a name");
    await api.post("/members", memberForm);
    setMemberForm({ name: "", role: "kid", avatar: "🧒" });
    await refreshMembers();
    onData?.();
    toast.success("Family member added 👋");
  };
  const delMember = async (id) => { await api.delete(`/members/${id}`); await refreshMembers(); onData?.(); };

  const AVATARS = ["🧒","👧","👦","👩","👨","🧑","👶","🐣","🦸","🦄"];

  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start">
      {/* Chore pool */}
      <div className="card-tactile p-6">
        <h2 className="text-xl font-extrabold mb-1" style={{ fontFamily: "Outfit" }}>🎡 Chore Wheel Pool</h2>
        <p className="text-slate-500 font-medium text-sm mb-4">Customize the chores, rewards and the fun animation each one triggers.</p>

        <form onSubmit={saveChore} className="grid grid-cols-2 gap-2 mb-5 p-3 rounded-xl bg-slate-50">
          <input data-testid="chore-title-input" placeholder="Chore name" value={choreForm.title}
            onChange={(e) => setChoreForm({ ...choreForm, title: e.target.value })}
            className="col-span-2 px-3 py-2 rounded-lg border-2 border-slate-200 outline-none font-medium" />
          <select data-testid="chore-theme-select" value={choreForm.theme}
            onChange={(e) => setChoreForm({ ...choreForm, theme: e.target.value })}
            className="px-3 py-2 rounded-lg border-2 border-slate-200 outline-none font-medium">
            {THEME_KEYS.map((k) => <option key={k} value={k}>{CHORE_THEMES[k].emojis[0]} {CHORE_THEMES[k].label}</option>)}
          </select>
          <input type="number" step="0.5" placeholder="Reward" value={choreForm.reward}
            onChange={(e) => setChoreForm({ ...choreForm, reward: e.target.value })}
            className="px-3 py-2 rounded-lg border-2 border-slate-200 outline-none font-medium" />
          <button data-testid="save-chore-btn" type="submit" className="btn-tactile bg-[#FF7A00] text-white col-span-2 py-2">
            {editingChore ? "Update chore" : "+ Add chore"}
          </button>
        </form>

        <div className="space-y-2 max-h-[420px] overflow-auto">
          {chores.map((c) => {
            const theme = CHORE_THEMES[c.theme] || CHORE_THEMES.washing_up;
            return (
              <div key={c.id} data-testid="manage-chore-row" className="flex items-center gap-2 p-2.5 rounded-xl border-2"
                style={{ background: theme.bg, borderColor: theme.color + "44" }}>
                <span className="text-xl">{theme.emojis[0]}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{c.title}</div>
                  <div className="text-[11px] font-semibold text-slate-500">{theme.label} · {fmtMoney(c.reward)}</div>
                </div>
                <button onClick={() => editChore(c)} className="text-slate-400 hover:text-slate-700 font-bold px-1">✎</button>
                <button onClick={() => delChore(c.id)} className="text-slate-300 hover:text-red-500 font-bold px-1">×</button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Members */}
      <div className="card-tactile p-6">
        <h2 className="text-xl font-extrabold mb-1" style={{ fontFamily: "Outfit" }}>👨‍👩‍👧‍👦 Family Members</h2>
        <p className="text-slate-500 font-medium text-sm mb-4">Add mom, dad and each kid slot.</p>

        <form onSubmit={addMember} className="grid grid-cols-2 gap-2 mb-5 p-3 rounded-xl bg-slate-50">
          <input data-testid="member-name-input" placeholder="Name" value={memberForm.name}
            onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
            className="col-span-2 px-3 py-2 rounded-lg border-2 border-slate-200 outline-none font-medium" />
          <select data-testid="member-role-select" value={memberForm.role}
            onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value })}
            className="px-3 py-2 rounded-lg border-2 border-slate-200 outline-none font-medium">
            <option value="kid">Kid</option>
            <option value="mom">Mom</option>
            <option value="dad">Dad</option>
          </select>
          <select value={memberForm.avatar} onChange={(e) => setMemberForm({ ...memberForm, avatar: e.target.value })}
            className="px-3 py-2 rounded-lg border-2 border-slate-200 outline-none font-medium text-xl">
            {AVATARS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <button data-testid="add-member-btn" type="submit" className="btn-tactile bg-[#10B981] text-white col-span-2 py-2">+ Add member</button>
        </form>

        <div className="space-y-2">
          {members.map((m) => (
            <div key={m.id} data-testid="manage-member-row" className="flex items-center gap-3 p-2.5 rounded-xl border-2 border-slate-100">
              <span className="w-9 h-9 rounded-full flex items-center justify-center text-lg" style={{ background: m.color + "33" }}>{m.avatar}</span>
              <div className="flex-1">
                <div className="font-bold" style={{ color: m.color }}>{m.name}</div>
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{m.role}</div>
              </div>
              <button onClick={() => delMember(m.id)} className="text-slate-300 hover:text-red-500 font-bold px-1">×</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
